




















































































































import { Component, Prop, Vue } from "vue-property-decorator";

import DatePicker, { DatePickerData } from "@/components/DatePicker.vue";
import TimeSlotPicker, { SlotObject } from "@/components/TimeSlotPicker.vue";
import VCheckbox from "@/components/VCheckbox.vue";
import GranularityRangePicker from "./GranularityRangePicker.vue";
import ReportNameField from "./ReportNameField.vue";
import {
  AutomaticReportFormValue,
  GranularityKind,
  GranularityRange,
  GRANULARITY_KIND_OPTIONS,
  WeekType,
  WEEK_TYPE_OPTIONS,
  DAY_SLOTS,
  MONTH_SLOTS,
  RangeSlot,
  MIN_QUANTITY,
  maxQuantityForKind,
  validateAutomaticReportName,
  isAutomaticReportFormValid,
} from "./automaticReport";

const DEFAULT_QUANTITY = 1;
const DEFAULT_WEEK_TYPE: WeekType = "mon_sun";

@Component({
  components: {
    GranularityRangePicker,
    DatePicker,
    TimeSlotPicker,
    VCheckbox,
    ReportNameField,
  },
})
export default class AutomaticReportForm extends Vue {
  @Prop({ required: true }) readonly value!: AutomaticReportFormValue;
  @Prop({ default: false }) readonly isVodOnly!: boolean;
  @Prop({ default: "" }) readonly maxDate!: string;

  readonly kindOptions = GRANULARITY_KIND_OPTIONS;
  readonly weekTypeOptions = WEEK_TYPE_OPTIONS;
  readonly minQuantity = MIN_QUANTITY;

  nameTouched = false;
  rangeTouched = false;
  showValidation = false;

  get name(): string {
    return this.value.name;
  }
  set name(name: string) {
    this.$emit("input", { ...this.value, name });
  }

  get granularityKind(): GranularityKind {
    return this.value.granularityKind;
  }
  set granularityKind(kind: GranularityKind) {
    this.onKindChange(kind);
  }

  get weekType(): WeekType {
    return this.value.weekType || DEFAULT_WEEK_TYPE;
  }
  set weekType(weekType: WeekType) {
    this.$emit("input", { ...this.value, weekType });
  }

  // Competitor sub-settings (period-adjacent; not inferred from the recurrence).
  get timeSlotsEnabled(): boolean {
    return this.value.timeSlots;
  }
  set timeSlotsEnabled(enabled: boolean) {
    const nextAutomaticReportForm: AutomaticReportFormValue = {
      ...this.value,
      timeSlots: enabled,
    };
    if (!enabled) nextAutomaticReportForm.timeSlotsFilter = [];
    this.$emit("input", nextAutomaticReportForm);
  }

  get timeSlotsFilter(): SlotObject[] {
    return this.value.timeSlotsFilter;
  }

  get compareEnabled(): boolean {
    return this.value.compare;
  }
  set compareEnabled(enabled: boolean) {
    const nextAutomaticReportForm: AutomaticReportFormValue = {
      ...this.value,
      compare: enabled,
    };
    if (!enabled) {
      delete nextAutomaticReportForm.comparisonStartsAt;
      delete nextAutomaticReportForm.comparisonEndsAt;
    }
    this.$emit("input", nextAutomaticReportForm);
  }

  get comparisonStartsAt(): string | undefined {
    return this.value.comparisonStartsAt;
  }

  get comparisonEndsAt(): string | undefined {
    return this.value.comparisonEndsAt;
  }

  onTimeSlotsInput(slots: SlotObject[]) {
    this.$emit("input", { ...this.value, timeSlotsFilter: slots });
  }

  onComparePeriodSelected(data: DatePickerData) {
    this.$emit("input", {
      ...this.value,
      comparisonStartsAt: data.range?.from?.toString(),
      comparisonEndsAt: data.range?.to?.toString(),
    });
  }

  get showQuantity(): boolean {
    return (
      this.value.granularityKind === "week" ||
      this.value.granularityKind === "year"
    );
  }

  get showRange(): boolean {
    return (
      this.value.granularityKind === "day" ||
      this.value.granularityKind === "month"
    );
  }

  get showWeekType(): boolean {
    return this.value.granularityKind === "week";
  }

  get rangeSlots(): RangeSlot[] {
    return this.value.granularityKind === "month" ? MONTH_SLOTS : DAY_SLOTS;
  }

  get rangeLabel(): string {
    return this.value.granularityKind === "month" ? "Mesi" : "Giorni";
  }

  get quantityMax(): number | null {
    return maxQuantityForKind(this.value.granularityKind);
  }

  get quantityHint(): string | null {
    if (this.value.granularityKind === "week") {
      return "Da 1 a 4 settimane: per periodi più lunghi seleziona «Mesi».";
    }
    if (this.value.granularityKind === "year") {
      return "Da 1 a 5 anni.";
    }
    return null;
  }

  get nameError(): string | null {
    return validateAutomaticReportName(this.value.name);
  }

  get rangeError(): string | null {
    if (!this.showRange || this.value.granularityRange) return null;
    return this.value.granularityKind === "month"
      ? "Seleziona un intervallo di mesi."
      : "Seleziona un intervallo di giorni.";
  }

  get showNameError(): boolean {
    return (this.nameTouched || this.showValidation) && !!this.nameError;
  }

  get showRangeError(): boolean {
    return (this.rangeTouched || this.showValidation) && !!this.rangeError;
  }

  get isValid(): boolean {
    return isAutomaticReportFormValid(this.value);
  }

  validate(): boolean {
    this.showValidation = true;
    return this.isValid;
  }

  // Back to a pristine pane: used when the parent clears fields on a still-mounted
  // form (after a create) so the emptied inputs don't flash their errors.
  resetValidation() {
    this.nameTouched = false;
    this.rangeTouched = false;
    this.showValidation = false;
  }

  onQuantityChange(event: Event) {
    const raw = (event.target as HTMLInputElement).value;
    const parsed = parseInt(raw, 10);
    let granularityQuantity =
      Number.isFinite(parsed) && parsed >= MIN_QUANTITY ? parsed : MIN_QUANTITY;
    if (this.quantityMax !== null && granularityQuantity > this.quantityMax) {
      granularityQuantity = this.quantityMax;
    }
    (event.target as HTMLInputElement).value = String(granularityQuantity);
    this.$emit("input", { ...this.value, granularityQuantity });
  }

  onRangeInput(range: GranularityRange | null) {
    this.rangeTouched = true;
    const nextAutomaticReportForm: AutomaticReportFormValue = {
      ...this.value,
    };
    if (range) {
      nextAutomaticReportForm.granularityRange = range;
    } else {
      delete nextAutomaticReportForm.granularityRange;
    }
    this.$emit("input", nextAutomaticReportForm);
  }

  onKindChange(granularityKind: GranularityKind) {
    this.rangeTouched = false;
    const nextAutomaticReportForm: AutomaticReportFormValue = {
      name: this.value.name,
      granularityKind,
      compare: this.value.compare,
      comparisonStartsAt: this.value.comparisonStartsAt,
      comparisonEndsAt: this.value.comparisonEndsAt,
      timeSlots: this.value.timeSlots,
      timeSlotsFilter: this.value.timeSlotsFilter,
    };
    if (granularityKind === "week" || granularityKind === "year") {
      nextAutomaticReportForm.granularityQuantity = DEFAULT_QUANTITY;
    }
    if (granularityKind === "week") {
      nextAutomaticReportForm.weekType = DEFAULT_WEEK_TYPE;
    }
    this.$emit("input", nextAutomaticReportForm);
  }
}
